/**
 * Shared helper: expand flat-dotted state keys into namespace prefixes.
 *
 * state.json uses flat dotted keys as a flat-namespace pattern:
 *   {
 *     "abcAnalysis.rows": { ... },
 *     "abcAnalysis.pageIndex": { ... }
 *   }
 *
 * References like `#{abcAnalysis.rows}` resolve against the namespace prefix
 * `abcAnalysis`. Without expansion, namespace lookups (`stateKeys.has('abcAnalysis')`)
 * return false and trigger false-positive "unknown namespace" warnings
 * (variable-ref-unknown, bind-state-missing, actionflow-compute-target-valid,
 * at-ref-undeclared, actionflow-unresolved-ref, boolean-binding-type-mismatch).
 *
 * This helper is the canonical fix for those six rules (~1872 warnings on
 * commerce-admin before the fix).
 */

/**
 * Given a set of state keys (flat-dotted or otherwise), return a new set that
 * also contains every leading prefix of each dotted key.
 *
 *   expandDottedStateKeys(new Set(['abcAnalysis.rows.headers']))
 *     → {'abcAnalysis', 'abcAnalysis.rows', 'abcAnalysis.rows.headers'}
 *
 * The original keys are always preserved.
 */
export function expandDottedStateKeys(keys: Iterable<string>): Set<string> {
  const out = new Set<string>()
  for (const key of keys) {
    out.add(key)
    if (key.includes(".")) {
      const parts = key.split(".")
      let acc = ""
      for (let i = 0; i < parts.length; i++) {
        acc = i === 0 ? parts[0] : acc + "." + parts[i]
        out.add(acc)
      }
    }
  }
  return out
}

/**
 * Expand a state DECLARATION map into every path the runtime will resolve.
 *
 * `expandDottedStateKeys` above handles one of the two nesting idioms the
 * corpus uses — the flat-dotted key (`"errors.email": {default: ""}`). It is
 * blind to the other: an object-typed declaration whose `default` carries the
 * shape (`"formData": {type: "object", default: {email: "", password: ""}}`).
 * `test/ontology-login-form` ships BOTH, and `bind-state-missing` fired on
 * `formData.email` while accepting `errors.email` — same nesting, two
 * spellings, one of them invisible.
 *
 * That is a false positive, derived from the consumer: `StateManager`'s
 * constructor does `this.store.set(key, structuredClone(varDef.default))`
 * (packages/cogo-runtime-v2/src/state-manager.ts), and `SymbolStore.get`
 * resolves a dotted path by splitting it and walking the nested value
 * (`getNestedValue(this.data, this.splitPath(path))`). So the runtime seeds
 * the object and reads `formData.email` out of it; only the validator's key
 * set could not see the member.
 *
 * Object defaults only. Array defaults are NOT indexed: the runtime can read
 * `items.0.name`, but a default's length is not a declaration of the paths a
 * bind may use, and inventing them would admit references nothing declares.
 */
export function expandDeclaredStateKeys(
  declarationMaps: Array<Record<string, unknown> | undefined>,
): Set<string> {
  const flat: string[] = []
  const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v)

  // Bounded: a declared default is a literal in the manifest, but the bound
  // keeps a pathological/self-referential structure from running away.
  const MAX_DEPTH = 8
  const addMembers = (prefix: string, value: unknown, depth: number): void => {
    if (depth > MAX_DEPTH || !isPlainObject(value)) return
    for (const [member, child] of Object.entries(value)) {
      const path = `${prefix}.${member}`
      flat.push(path)
      addMembers(path, child, depth + 1)
    }
  }

  for (const declarations of declarationMaps) {
    for (const [key, def] of Object.entries(declarations ?? {})) {
      flat.push(key)
      if (isPlainObject(def) && "default" in def) addMembers(key, def.default, 1)
    }
  }
  // Still run the dotted expansion: it supplies the PREFIXES of flat-dotted
  // keys, which member expansion does not produce.
  return expandDottedStateKeys(flat)
}
